import React from 'react';
import { motion } from 'framer-motion';

const Hero = () => {
  return (
    <section className="relative h-[100svh] min-h-[700px] w-full flex items-center overflow-hidden bg-[#F9F8F6]">
      {/* Main Dominant Image Container */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-black/10 z-10" />
        <picture>
          <source 
            type="image/webp" 
            srcSet="https://images.unsplash.com/photo-1616489953149-805966403602?auto=format&fit=crop&w=800&q=80 800w, https://images.unsplash.com/photo-1616489953149-805966403602?auto=format&fit=crop&w=1600&q=80 1600w" 
            sizes="100vw" 
          />
          <img 
            src="https://images.unsplash.com/photo-1616489953149-805966403602?auto=format&fit=crop&w=1600&q=80" 
            alt="Contemporary living room with layered textures and warm morning light" 
            fetchPriority="high"
            className="w-full h-full object-cover object-center animate-[slowZoom_30s_ease-in-out_infinite_alternate]"
            width="1600"
            height="900"
          />
        </picture>
      </div>

      <div className="relative z-20 w-full max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Hero Text Content */}
        <div className="lg:col-span-7 xl:col-span-6 mt-12 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          >
            <p className="text-white lg:text-[#8B7355] text-xs md:text-sm tracking-[0.3em] mb-6 uppercase font-medium drop-shadow-sm lg:drop-shadow-none">
              A Study in Texture
            </p>
            <h1 className="text-5xl md:text-7xl xl:text-8xl font-serif text-white lg:text-[#2C2C2C] mb-8 leading-[1.1] text-balance drop-shadow-md lg:drop-shadow-none">
              The Art of <br /> Slow Living
            </h1>
            <p className="text-white/90 lg:text-[#595959] text-base md:text-lg max-w-lg mb-10 font-light leading-relaxed drop-shadow-sm lg:drop-shadow-none">
              Discover a curated collection of intentional home goods designed to ground your space and elevate your daily rituals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#lookbook" 
                className="inline-block bg-[#2C2C2C] text-white px-10 py-4 text-xs md:text-sm tracking-[0.2em] uppercase hover:bg-black transition-all duration-300 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2"
              >
                Shop Lookbook
              </a>
              <a 
                href="#bundles" 
                className="inline-block border border-white lg:border-[#2C2C2C] text-white lg:text-[#2C2C2C] px-10 py-4 text-xs md:text-sm tracking-[0.2em] uppercase hover:bg-white lg:hover:bg-[#2C2C2C] hover:text-[#2C2C2C] lg:hover:text-white transition-all duration-300 text-center backdrop-blur-sm lg:backdrop-blur-none"
              >
                View Sets
              </a>
            </div>
          </motion.div>
        </div>

        {/* Supporting Product Crop */}
        <div className="hidden lg:block lg:col-span-5 xl:col-start-9 xl:col-span-4">
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="relative"
          >
            <div className="aspect-[3/4] bg-[#EFECE8] rounded-sm overflow-hidden shadow-2xl border-[12px] border-white/10 backdrop-blur-md">
              <img 
                src="https://images.unsplash.com/photo-1594026112284-02bb6f3352fe?auto=format&fit=crop&w=600&q=80" 
                alt="Close-up detail of textured linen cushion and woven throw" 
                className="w-full h-full object-cover"
                loading="lazy"
                width="600"
                height="800"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-5 shadow-lg">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#8B7355] mb-1 font-medium">Featured Detail</p>
                <h3 className="text-sm font-serif text-[#2C2C2C]">Organic Linen Cushion Set</h3>
                <p className="text-xs text-[#737373] mt-2 font-light">Hand-loomed in small batches.</p>
              </div>
            </div>
            
            {/* Decorative element */}
            <div className="absolute -top-6 -right-6 w-24 h-24 border-t border-r border-white/30 hidden xl:block" />
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center pointer-events-none">
        <span className="text-[10px] tracking-[0.4em] uppercase text-white/60 mb-4 hidden md:block">Scroll</span>
        <span className="w-[1px] h-12 bg-white/20 block relative overflow-hidden">
          <span className="w-full h-full bg-white absolute top-0 left-0 animate-[scrollDown_2.5s_cubic-bezier(0.645,0.045,0.355,1)_infinite]" />
        </span>
      </div>
    </section>
  );
};

export default Hero;